import React, { memo, useMemo, useState } from 'react';
import { CaretDown, CaretUp, Eye, Warning } from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';
import { resolvePreviewContact } from '../utils/previewContact';

/**
 * Inline preview panel showing the resolved values of every placeholder that
 * actually appears in the current subject/content. Lets the user verify that
 * `{totalDebt}`, `{buildingName}`, `{contact.firstName}` etc. will render the
 * correct data without having to switch to the "Voorbeeld" tab.
 *
 * Only renders when there is at least one placeholder used in the subject or
 * body — otherwise it would just be visual noise.
 */
type PlaceholderRow = {
  placeholder: string;
  source: 'context' | 'contact';
  value: string;
  filled: boolean;
};

const PLACEHOLDER_REGEX = /\{[A-Za-z0-9._]+\}/g;

export const PlaceholderPreview: React.FC = memo(() => {
  const { state } = useMassMailContext();
  const [expanded, setExpanded] = useState(true);

  const previewContact = useMemo(() => {
    const resolved = resolvePreviewContact(state.contacts, state.selectedPreviewContactId);
    // Hier geen verzonnen voorbeelddata: lege waarden moeten zichtbaar blijven.
    return resolved.isDummy ? null : resolved.contact;
  }, [state.contacts, state.selectedPreviewContactId]);

  const rows = useMemo<PlaceholderRow[]>(() => {
    const haystack = `${state.emailSubject ?? ''}\n${state.emailContent ?? ''}`;
    const matches = haystack.match(PLACEHOLDER_REGEX);
    if (!matches) return [];

    const seen = new Set<string>();
    const unique = matches.filter((m) => {
      if (seen.has(m)) return false;
      seen.add(m);
      return true;
    });

    const contactValueMap: Record<string, string | undefined> = previewContact
      ? {
          '{contact.firstName}': previewContact.firstName,
          '{contact.lastName}': previewContact.lastName,
          '{contact.fullName}': previewContact.name,
          '{contact.email}': previewContact.email,
          '{contact.phone}': (previewContact as any).phoneMobile,
          '{Voornaam}': previewContact.firstName,
          '{Achternaam}': previewContact.lastName,
          '{VolledigeNaam}': previewContact.name,
          '{Email}': previewContact.email,
          '{MobielNummer}': (previewContact as any).phoneMobile,
        }
      : {};

    // Build a lookup from placeholder string → resolved value from state.parameters
    const paramValueMap: Record<string, string> = {};
    if (state.parameters && state.parameterValues) {
      for (const param of state.parameters) {
        const raw = state.parameterValues[param.id];
        if (param.type === 'global' && typeof raw === 'string') {
          paramValueMap[param.placeholder] = raw;
        } else if (param.type === 'individual' && typeof raw === 'object' && raw !== null && previewContact) {
          const perContact = raw as Record<string, string>;
          const v = perContact[String(previewContact.id)] ?? '';
          paramValueMap[param.placeholder] = v;
        }
      }
    }

    return unique
      .map<PlaceholderRow | null>((placeholder) => {
        const key = placeholder.slice(1, -1);
        if (placeholder in contactValueMap) {
          const v = contactValueMap[placeholder] ?? '';
          return { placeholder, source: 'contact', value: v, filled: !!v.trim() };
        }
        if (placeholder in paramValueMap) {
          const v = paramValueMap[placeholder];
          return { placeholder, source: 'contact', value: v, filled: !!v.trim() };
        }
        if (state.contextPlaceholders && key in state.contextPlaceholders) {
          const v = String(state.contextPlaceholders[key] ?? '');
          return { placeholder, source: 'context', value: v, filled: !!v.trim() };
        }
        // Unknown placeholders: still surface them so the user can spot typos.
        return { placeholder, source: 'context', value: '', filled: false };
      })
      .filter((r): r is PlaceholderRow => r !== null);
  }, [state.emailSubject, state.emailContent, state.contextPlaceholders, state.parameters, state.parameterValues, previewContact]);

  if (rows.length === 0) return null;

  const missingCount = rows.filter((r) => !r.filled).length;

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-blue-900 hover:bg-blue-100/50 rounded-lg transition-colors"
      >
        <span className="flex items-center gap-2">
          <Eye size={16} weight="fill" />
          Voorbeeld waarden
          <span className="text-xs font-normal text-blue-700">
            ({rows.length} variabele{rows.length === 1 ? '' : 'n'}
            {missingCount > 0 ? `, ${missingCount} leeg` : ''})
          </span>
        </span>
        {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
      </button>

      {expanded && (
        <div className="border-t border-blue-200 px-3 py-2">
          <p className="mb-2 text-xs text-blue-800">
            Controleer of de waarden hieronder correct zijn vóór verzenden. Wijzig de gegevens van het dossier of contact als iets fout staat.
          </p>
          <div className="overflow-hidden rounded-md border border-blue-200 bg-white">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/50 text-left text-blue-900">
                  <th className="px-3 py-1.5 font-medium">Variabele</th>
                  <th className="px-3 py-1.5 font-medium">Waarde</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.placeholder}
                    className="border-b border-blue-100 last:border-b-0"
                  >
                    <td className="px-3 py-1.5 align-top">
                      <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[11px] text-neutral-800">
                        {row.placeholder}
                      </code>
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-neutral-secondary-mid">
                        {row.source === 'context' ? 'Dossier' : 'Contact'}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 align-top">
                      {row.filled ? (
                        <span className="font-medium text-neutral-900">{row.value}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-700">
                          <Warning size={11} weight="fill" />
                          Leeg
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
});
